import { useState, useEffect, useCallback, useRef } from 'react';
import { PortalLayout } from '../components/PortalLayout';

/* ── API helpers ──────────────────────────────────────────────────── */

const BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : '/api';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('tourx_token');
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

async function apiFetch<T>(url: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${url}`, { headers: authHeaders(), ...opts });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(typeof body.detail === 'string' ? body.detail : `API error ${res.status}`);
  }
  return res.json();
}

/* ── Types ─────────────────────────────────────────────────────────── */

interface Customer {
  id: number;
  customer_name: string;
  contact_name?: string;
  phone?: string;
  address?: string;
  postcode?: string;
}

interface DriverOption {
  id: number;
  first_names: string;
  surname: string;
}

interface Site {
  id: number;
  code: string;
  name: string;
  address: string;
  postcode: string;
}

interface ItineraryRow {
  date: string;
  time: string;
  type: string;
  site_id: number | null;   // AMZ FC site id, null = custom entry
  location: string;          // FC name or custom postcode
  resolved_address: string;  // Full address
  lat: number | null;
  lng: number | null;
  notes: string;
  pallets: string;
}

interface JobSheetForm {
  id: number | null;
  js_number: string;
  status: string;
  /* client */
  customer_id: number | null;
  client_name: string;
  client_address: string;
  client_contact: string;
  client_tel: string;
  ref1: string;
  ref2: string;
  /* job */
  job_date: string;
  description: string;
  licence_type: string;
  driver_id: number | null;
  /* vehicle */
  vehicle_type: string;
  reg_mark: string;
  fleet_no: string;
  /* first drop */
  first_drop_site_id: number | null;
  first_drop_address: string;
  destination_postcode: string;
  single_journey: boolean;
  vehicle_to_stay: boolean;
  total_distance: string;
  empty_distance: string;
  total_load_weight: string;
  /* emergency contact */
  emergency_name: string;
  emergency_tel: string;
  /* itinerary */
  itinerary: ItineraryRow[];
}

interface JobSheetSummary {
  id: number;
  js_number: string;
  job_date: string;
  client_name: string;
  driver_name: string | null;
  description: string;
  status: string;
}

const EMPTY_ROW: ItineraryRow = {
  date: '', time: '', type: '', site_id: null, location: '', resolved_address: '', lat: null, lng: null, notes: '', pallets: '',
};

const ITINERARY_TYPES = ['Start', 'First Drop', 'Position', 'Pick-up', 'Arrival', 'Break', 'Leave', 'Back', 'Finish'];

const DESCRIPTION_OPTIONS = ['CK LOG AMAZON FC DROP', 'GENERAL HAULAGE WORK'];
const LICENCE_OPTIONS = ['CLASS 2', 'CLASS 1'];

/* ── Fixed defaults from Work Ticket template (BLACK fields) ──── */
const HQ_ADDRESS = 'Unit 3A Soloman Park, Soloman Road, Ilkeston, Derbyshire, United Kingdom, DE7 5UE';
const HQ_POSTCODE = 'DE7 5UE';
const HQ_LAT = 52.9710;
const HQ_LNG = -1.3090;

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  issued: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
};

function errorMsg(e: unknown): string {
  if (e instanceof Error) return e.message;
  return typeof e === 'string' ? e : String(e);
}

function emptyForm(): JobSheetForm {
  const today = new Date().toISOString().slice(0, 10);
  return {
    id: null,
    js_number: '',
    status: 'draft',
    customer_id: null,
    client_name: '',
    client_address: HQ_ADDRESS,
    client_contact: 'Jordan Fletcher',
    client_tel: '07751 971813',
    ref1: '',
    ref2: '',
    job_date: today,
    description: DESCRIPTION_OPTIONS[0],
    licence_type: LICENCE_OPTIONS[0],  // CLASS 2
    driver_id: null,
    vehicle_type: 'DAF LF 150 7.5T TAIL LIFT',
    reg_mark: 'LK67 ZYT',
    fleet_no: 'ZYT',
    first_drop_site_id: null,
    first_drop_address: '',
    destination_postcode: HQ_POSTCODE,
    single_journey: false,
    vehicle_to_stay: false,
    total_distance: '',
    empty_distance: '',
    total_load_weight: '',
    emergency_name: 'Matthew Limb',
    emergency_tel: '07306 783144',
    itinerary: [
      { ...EMPTY_ROW, date: today, type: 'Start', site_id: null, location: HQ_ADDRESS, lat: HQ_LAT, lng: HQ_LNG },
      { ...EMPTY_ROW, date: today, type: 'Finish', site_id: null, location: HQ_ADDRESS, lat: HQ_LAT, lng: HQ_LNG },
    ],
  };
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════════ */

export function JobSheetsPage() {
  /* ── View state ─────────────────────────────────────────────────── */
  const [view, setView] = useState<'list' | 'form'>('list');

  /* ── List state ─────────────────────────────────────────────────── */
  const [items, setItems] = useState<JobSheetSummary[]>([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  /* ── Form state ─────────────────────────────────────────────────── */
  const [form, setForm] = useState<JobSheetForm>(emptyForm());
  const [saving, setSaving] = useState(false);

  /* ── Lookup data ────────────────────────────────────────────────── */
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [sites, setSites] = useState<Site[]>([]);

  /* ── Distance calc debounce ─────────────────────────────────────── */
  const calcTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ── Load list ──────────────────────────────────────────────────── */
  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const qs = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const data = await apiFetch<JobSheetSummary[]>(`/jobsheets/${qs}`);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(errorMsg(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { if (view === 'list') loadList(); }, [view, loadList]);

  /* ── Load lookup data (customers, drivers, sites) ───────────────── */
  useEffect(() => {
    apiFetch<Customer[]>('/quotes/customers').then(setCustomers).catch(() => {});
    apiFetch<any>('/drivers?page_size=500').then(d => {
      const list = Array.isArray(d) ? d : d.drivers || [];
      setDrivers(list);
    }).catch(() => {});
    apiFetch<Site[]>('/jobsheets/sites').then(setSites).catch(() => setSites([]));
  }, []);

  /* ── Auto-calculate distance when itinerary stops have coords ──── */
  useEffect(() => {
    if (view !== 'form') return;
    const resolved = form.itinerary.filter(r => r.lat != null && r.lng != null);
    if (resolved.length < 2) return;

    // Debounce to avoid excessive API calls
    if (calcTimerRef.current) clearTimeout(calcTimerRef.current);
    calcTimerRef.current = setTimeout(async () => {
      try {
        const postcodes = resolved.map(r => {
          if (r.site_id && r.site_id > 0) {
            const site = sites.find(s => s.id === r.site_id);
            return site?.postcode || '';
          }
          // For HQ or custom entries, extract postcode from end of location string
          const loc = r.location || '';
          const match = loc.match(/([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\s*$/i);
          return match ? match[1].toUpperCase() : loc;
        }).filter(Boolean);

        if (postcodes.length < 2) return;

        const result = await apiFetch<{ total_distance: number; empty_distance: number }>(
          '/jobsheets/calc-distance',
          { method: 'POST', body: JSON.stringify({ postcodes }) },
        );
        setForm(prev => ({
          ...prev,
          total_distance: String(result.total_distance ?? ''),
          empty_distance: String(result.empty_distance ?? ''),
        }));
      } catch {
        // Distance calc failed — leave fields editable
      }
    }, 800);

    return () => {
      if (calcTimerRef.current) clearTimeout(calcTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.itinerary, sites, view]);

  /* ── Auto-set destination to last FC drop in itinerary ──────── */
  useEffect(() => {
    if (view !== 'form') return;
    // Find the last itinerary row with a site_id (FC drop), excluding Start/Finish
    const fcRows = form.itinerary.filter(r => r.site_id != null && r.site_id > 0);
    if (fcRows.length > 0) {
      const lastFc = fcRows[fcRows.length - 1];
      const site = sites.find(s => s.id === lastFc.site_id);
      if (site) {
        setForm(prev => {
          if (prev.destination_postcode !== site.postcode) {
            return { ...prev, destination_postcode: site.postcode };
          }
          return prev;
        });
      }
    }
  }, [form.itinerary, sites, view]);

  /* ── Open create form ───────────────────────────────────────────── */
  const handleNew = async () => {
    const f = emptyForm();
    // Generate 6-digit random Job ID
    f.js_number = String(Math.floor(100000 + Math.random() * 900000));
    setForm(f);
    setView('form');
  };

  /* ── Open edit form ─────────────────────────────────────────────── */
  const handleEdit = async (id: number) => {
    try {
      setLoading(true);
      const data = await apiFetch<any>(`/jobsheets/${id}`);
      setForm({
        id: data.id,
        js_number: data.js_number || '',
        status: data.status || 'draft',
        customer_id: data.customer_id ?? null,
        client_name: data.client_name || '',
        client_address: data.client_address || '',
        client_contact: data.client_contact || '',
        client_tel: data.client_tel || '',
        ref1: data.ref1 || '',
        ref2: data.ref2 || '',
        job_date: data.job_date || '',
        description: data.description || DESCRIPTION_OPTIONS[0],
        licence_type: data.licence_type || LICENCE_OPTIONS[0],
        driver_id: data.driver_id ?? null,
        vehicle_type: data.vehicle_type || '',
        reg_mark: data.reg_mark || '',
        fleet_no: data.fleet_no || '',
        first_drop_site_id: data.first_drop_site_id ?? null,
        first_drop_address: data.first_drop_address || '',
        destination_postcode: data.destination_postcode || '',
        single_journey: data.single_journey ?? false,
        vehicle_to_stay: data.vehicle_to_stay ?? false,
        total_distance: data.total_distance != null ? String(data.total_distance) : '',
        empty_distance: data.empty_distance != null ? String(data.empty_distance) : '',
        total_load_weight: data.total_load_weight || '',
        emergency_name: data.emergency_name || 'Matthew Limb',
        emergency_tel: data.emergency_tel || '07306 783144',
        itinerary: Array.isArray(data.itinerary) && data.itinerary.length > 0
          ? data.itinerary.map((r: any) => ({
              date: r.date || '',
              time: r.time || '',
              type: r.type || '',
              // If backend returns site_id=null but has a location, it's a custom entry → use -1 sentinel
              site_id: r.site_id != null ? r.site_id : (r.location ? -1 : null),
              location: r.location || '',
              resolved_address: r.resolved_address || '',
              lat: r.lat ?? null,
              lng: r.lng ?? null,
              notes: r.notes || '',
              pallets: r.pallets || '',
            }))
          : [{ ...EMPTY_ROW, date: data.job_date || '', type: 'Start' }],
      });
      setView('form');
    } catch (e) {
      setError(errorMsg(e));
    } finally {
      setLoading(false);
    }
  };

  /* ── Delete ─────────────────────────────────────────────────────── */
  const handleDelete = async (id: number) => {
    if (!confirm('Delete this job sheet?')) return;
    try {
      await apiFetch(`/jobsheets/${id}`, { method: 'DELETE' });
      setSuccess('Job sheet deleted.');
      loadList();
    } catch (e) {
      setError(errorMsg(e));
    }
  };

  /* ── Download PDF ───────────────────────────────────────────────── */
  const handlePdf = (id: number) => {
    const token = localStorage.getItem('tourx_token');
    const url = `${BASE}/jobsheets/${id}/pdf${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    window.open(url, '_blank');
  };

  /* ── Save (draft or issue) ──────────────────────────────────────── */
  const handleSave = async (issue: boolean) => {
    try {
      setSaving(true);
      setError('');
      const payload: any = {
        ...form,
        total_distance: form.total_distance ? parseFloat(form.total_distance) : null,
        empty_distance: form.empty_distance ? parseFloat(form.empty_distance) : null,
        total_load_weight: form.total_load_weight || null,
        status: issue ? 'issued' : 'draft',
        // Normalize site_id: -1 (custom sentinel) → null for the backend
        itinerary: form.itinerary.map(r => ({
          ...r,
          site_id: r.site_id === -1 ? null : r.site_id,
        })),
      };
      delete payload.id;

      if (form.id) {
        await apiFetch(`/jobsheets/${form.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        if (issue && form.status !== 'issued') {
          await apiFetch(`/jobsheets/${form.id}/status`, { method: 'PUT', body: JSON.stringify({ status: 'issued' }) });
        }
      } else {
        const created = await apiFetch<any>('/jobsheets/', { method: 'POST', body: JSON.stringify(payload) });
        setForm(prev => ({ ...prev, id: created.id }));
        if (issue) {
          await apiFetch(`/jobsheets/${created.id}/status`, { method: 'PUT', body: JSON.stringify({ status: 'issued' }) });
        }
      }
      setSuccess(issue ? 'Job sheet issued.' : 'Job sheet saved as draft.');
      setView('list');
    } catch (e) {
      setError(errorMsg(e));
    } finally {
      setSaving(false);
    }
  };

  /* ── Customer selection ─────────────────────────────────────────── */
  const handleCustomerChange = (custId: string) => {
    const id = custId ? parseInt(custId, 10) : null;
    const cust = customers.find(c => c.id === id);
    setForm(prev => ({
      ...prev,
      customer_id: id,
      client_name: cust?.customer_name || prev.client_name,
      client_address: cust?.address || prev.client_address,
      client_contact: cust?.contact_name || prev.client_contact,
      client_tel: cust?.phone || prev.client_tel,
    }));
  };

  /* ── Site selection (first drop) — also auto-inserts into itinerary */
  const handleSiteChange = async (siteId: string) => {
    const id = siteId ? parseInt(siteId, 10) : null;
    const site = sites.find(s => s.id === id);
    const fullAddr = site ? `${site.name}, ${site.address}` : '';

    setForm(prev => {
      const updated = {
        ...prev,
        first_drop_site_id: id,
        first_drop_address: fullAddr,
      };

      // Auto-insert the FC into the itinerary (between Start and Finish)
      if (site) {
        const rows = [...prev.itinerary];
        // Remove any previously auto-inserted first-drop row
        const cleaned = rows.filter(r => r.type !== 'First Drop');
        // Find the Finish row (always last) or fallback to end
        const finishIdx = cleaned.findIndex(r => r.type === 'Finish');
        const insertAt = finishIdx >= 0 ? finishIdx : cleaned.length;
        const newRow: ItineraryRow = {
          date: prev.job_date,
          time: '',
          type: 'First Drop',
          site_id: id,
          location: fullAddr,
          resolved_address: site.address,
          lat: null,
          lng: null,
          notes: '',
          pallets: '',
        };
        cleaned.splice(insertAt, 0, newRow);
        updated.itinerary = cleaned;
      }

      return updated;
    });

    // Look up lat/lng for the inserted FC row
    if (site) {
      try {
        const result = await apiFetch<{ lat: number; lng: number }>(
          '/jobsheets/postcode-lookup',
          { method: 'POST', body: JSON.stringify({ postcode: site.postcode }) },
        );
        setForm(prev => {
          const rows = prev.itinerary.map(r =>
            r.type === 'First Drop' && r.site_id === id
              ? { ...r, lat: result.lat, lng: result.lng }
              : r,
          );
          return { ...prev, itinerary: rows };
        });
      } catch { /* postcode lookup failed */ }
    }
  };

  /* ── Itinerary helpers ──────────────────────────────────────────── */
  const updateRow = (idx: number, field: keyof ItineraryRow, value: string | number | null) => {
    setForm(prev => {
      const rows = [...prev.itinerary];
      rows[idx] = { ...rows[idx], [field]: value };
      return { ...prev, itinerary: rows };
    });
  };

  const addRow = () => {
    setForm(prev => ({
      ...prev,
      itinerary: [...prev.itinerary, { ...EMPTY_ROW, date: prev.job_date }],
    }));
  };

  const removeRow = (idx: number) => {
    setForm(prev => ({
      ...prev,
      itinerary: prev.itinerary.filter((_, i) => i !== idx),
    }));
  };

  const moveRow = (idx: number, direction: 'up' | 'down') => {
    setForm(prev => {
      const rows = [...prev.itinerary];
      const target = direction === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= rows.length) return prev;
      [rows[idx], rows[target]] = [rows[target], rows[idx]];
      return { ...prev, itinerary: rows };
    });
  };

  /* ── Itinerary: FC site selection for a row ─────────────────────── */
  const handleItinerarySiteChange = async (idx: number, siteIdStr: string) => {
    if (siteIdStr === 'custom') {
      // Switch to custom mode — use site_id = -1 as sentinel
      setForm(prev => {
        const rows = [...prev.itinerary];
        rows[idx] = { ...rows[idx], site_id: -1, location: '', resolved_address: '', lat: null, lng: null };
        return { ...prev, itinerary: rows };
      });
      return;
    }

    if (!siteIdStr) {
      // Nothing selected — reset row location
      setForm(prev => {
        const rows = [...prev.itinerary];
        rows[idx] = { ...rows[idx], site_id: null, location: '', resolved_address: '', lat: null, lng: null };
        return { ...prev, itinerary: rows };
      });
      return;
    }

    const siteId = parseInt(siteIdStr, 10);
    const site = sites.find(s => s.id === siteId);
    if (!site) return;

    // Set the FC site data immediately — use FULL address as location
    setForm(prev => {
      const rows = [...prev.itinerary];
      rows[idx] = {
        ...rows[idx],
        site_id: siteId,
        location: `${site.name}, ${site.address}`,
        resolved_address: site.address,
      };
      return { ...prev, itinerary: rows };
    });

    // Look up lat/lng from the site's postcode
    try {
      const result = await apiFetch<{ lat: number; lng: number }>(
        '/jobsheets/postcode-lookup',
        { method: 'POST', body: JSON.stringify({ postcode: site.postcode }) },
      );
      setForm(prev => {
        const rows = [...prev.itinerary];
        if (rows[idx]?.site_id === siteId) {
          rows[idx] = { ...rows[idx], lat: result.lat, lng: result.lng };
        }
        return { ...prev, itinerary: rows };
      });
    } catch {
      // Postcode lookup failed — distances won't auto-calculate for this stop
    }
  };

  /* ── Postcode lookup for custom itinerary row ──────────────────── */
  const lookupCustomPostcode = async (idx: number) => {
    const pc = form.itinerary[idx].location.trim();
    if (!pc) return;
    try {
      const result = await apiFetch<{ address: string; lat: number; lng: number }>(
        '/jobsheets/postcode-lookup',
        { method: 'POST', body: JSON.stringify({ postcode: pc }) },
      );
      setForm(prev => {
        const rows = [...prev.itinerary];
        rows[idx] = {
          ...rows[idx],
          resolved_address: result.address,
          lat: result.lat,
          lng: result.lng,
        };
        return { ...prev, itinerary: rows };
      });
    } catch {
      // Silently fail — user can still proceed
    }
  };

  /* ── Dismiss banners ────────────────────────────────────────────── */
  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(''), 4000); return () => clearTimeout(t); }
  }, [success]);
  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(''), 6000); return () => clearTimeout(t); }
  }, [error]);

  /* ══════════════════════════════════════════════════════════════════
     RENDER
     ══════════════════════════════════════════════════════════════════ */

  return (
    <PortalLayout currentPage="jobsheets">
      <div className="p-5">
        {/* ── Banners ─────────────────────────────────────────────── */}
        {success && (
          <div className="mb-4 px-4 py-2.5 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm flex items-center justify-between">
            <span>{success}</span>
            <button onClick={() => setSuccess('')} className="text-green-600 hover:text-green-800 ml-3">&times;</button>
          </div>
        )}
        {error && (
          <div className="mb-4 px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="text-red-600 hover:text-red-800 ml-3">&times;</button>
          </div>
        )}

        {view === 'list' ? (
          /* ════════════════════════════════════════════════════════════
             LIST VIEW
             ════════════════════════════════════════════════════════════ */
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <div>
                <h1 className="text-sm font-semibold uppercase tracking-wide text-gray-900 mb-1">Job Sheets</h1>
                <p className="text-gray-500 text-sm">Create and manage driver job sheets</p>
              </div>
              <button
                onClick={handleNew}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                + New Job Sheet
              </button>
            </div>

            {/* Status filter tabs */}
            <div className="flex gap-1 mb-5 border-b border-gray-200">
              {['all', 'draft', 'issued', 'completed'].map(s => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors capitalize ${
                    statusFilter === s
                      ? 'border-blue-500 text-blue-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {loading ? (
                <div className="p-10 text-center text-gray-400 text-sm">Loading...</div>
              ) : items.length === 0 ? (
                <div className="p-10 text-center text-gray-400 text-sm">No job sheets found.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left">
                      <th className="px-4 py-3 font-medium text-gray-600">JS Number</th>
                      <th className="px-4 py-3 font-medium text-gray-600">Date</th>
                      <th className="px-4 py-3 font-medium text-gray-600">Client</th>
                      <th className="px-4 py-3 font-medium text-gray-600">Driver</th>
                      <th className="px-4 py-3 font-medium text-gray-600">Description</th>
                      <th className="px-4 py-3 font-medium text-gray-600">Status</th>
                      <th className="px-4 py-3 font-medium text-gray-600 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => (
                      <tr
                        key={item.id}
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => handleEdit(item.id)}
                      >
                        <td className="px-4 py-3 font-medium text-gray-900">{item.js_number}</td>
                        <td className="px-4 py-3 text-gray-600">{item.job_date || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{item.client_name || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{item.driver_name || '-'}</td>
                        <td className="px-4 py-3 text-gray-600 truncate max-w-[200px]">{item.description || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[item.status] || 'bg-gray-100 text-gray-700'}`}>
                            {item.status?.charAt(0).toUpperCase() + item.status?.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                            <button
                              onClick={() => handleEdit(item.id)}
                              className="px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handlePdf(item.id)}
                              className="px-2.5 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded transition-colors"
                            >
                              PDF
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="px-2.5 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        ) : (
          /* ════════════════════════════════════════════════════════════
             FORM VIEW
             ════════════════════════════════════════════════════════════ */
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <div>
                <h1 className="text-sm font-semibold uppercase tracking-wide text-gray-900 mb-1">
                  {form.id ? `Edit Job Sheet — ${form.js_number}` : 'New Job Sheet'}
                </h1>
                <p className="text-gray-500 text-sm">
                  {form.id ? 'Update job sheet details' : 'Create a new driver job sheet'}
                </p>
              </div>
              <button
                onClick={() => setView('list')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Back to List
              </button>
            </div>

            <div className="space-y-5">

              {/* ── Client Section ─────────────────────────────────── */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="text-sm font-semibold text-gray-900 mb-4">Client Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Customer dropdown */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Customer</label>
                    <select
                      value={form.customer_id ?? ''}
                      onChange={e => handleCustomerChange(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    >
                      <option value="">-- Select customer --</option>
                      {customers.map(c => (
                        <option key={c.id} value={c.id}>{c.customer_name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Client Name</label>
                    <input
                      type="text"
                      value={form.client_name}
                      onChange={e => setForm(p => ({ ...p, client_name: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Contact</label>
                    <input
                      type="text"
                      value={form.client_contact}
                      onChange={e => setForm(p => ({ ...p, client_contact: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                    <input
                      type="text"
                      value={form.client_address}
                      onChange={e => setForm(p => ({ ...p, client_address: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Telephone</label>
                    <input
                      type="text"
                      value={form.client_tel}
                      onChange={e => setForm(p => ({ ...p, client_tel: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Ref 1</label>
                    <input
                      type="text"
                      value={form.ref1}
                      onChange={e => setForm(p => ({ ...p, ref1: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Ref 2</label>
                    <input
                      type="text"
                      value={form.ref2}
                      onChange={e => setForm(p => ({ ...p, ref2: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* ── Emergency Contact ─────────────────────────────── */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="text-sm font-semibold text-gray-900 mb-4">Emergency Contact</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                    <input
                      type="text"
                      value={form.emergency_name}
                      onChange={e => setForm(p => ({ ...p, emergency_name: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Telephone</label>
                    <input
                      type="text"
                      value={form.emergency_tel}
                      onChange={e => setForm(p => ({ ...p, emergency_tel: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* ── Job Details ────────────────────────────────────── */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="text-sm font-semibold text-gray-900 mb-4">Job Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">JS Number</label>
                    <input
                      type="text"
                      value={form.js_number}
                      onChange={e => setForm(p => ({ ...p, js_number: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Job Date</label>
                    <input
                      type="date"
                      value={form.job_date}
                      onChange={e => {
                        const d = e.target.value;
                        setForm(p => ({
                          ...p,
                          job_date: d,
                          itinerary: p.itinerary.map(r => ({ ...r, date: d })),
                        }));
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                    <select
                      value={form.description}
                      onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    >
                      {DESCRIPTION_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Licence Type</label>
                    <select
                      value={form.licence_type}
                      onChange={e => setForm(p => ({ ...p, licence_type: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    >
                      {LICENCE_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Driver</label>
                    <select
                      value={form.driver_id ?? ''}
                      onChange={e => setForm(p => ({ ...p, driver_id: e.target.value ? parseInt(e.target.value, 10) : null }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    >
                      <option value="">-- Select driver --</option>
                      {drivers.map(d => (
                        <option key={d.id} value={d.id}>{d.first_names} {d.surname}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* ── Vehicle Details ────────────────────────────────── */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="text-sm font-semibold text-gray-900 mb-4">Vehicle Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Vehicle Type</label>
                    <input
                      type="text"
                      placeholder="e.g. DAF LF 150 7.5T TAIL LIFT"
                      value={form.vehicle_type}
                      onChange={e => setForm(p => ({ ...p, vehicle_type: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Reg Mark</label>
                    <input
                      type="text"
                      value={form.reg_mark}
                      onChange={e => setForm(p => ({ ...p, reg_mark: e.target.value.toUpperCase() }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm uppercase focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Fleet No</label>
                    <input
                      type="text"
                      value={form.fleet_no}
                      onChange={e => setForm(p => ({ ...p, fleet_no: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* ── First Drop Off ─────────────────────────────────── */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="text-sm font-semibold text-gray-900 mb-4">First Drop Off</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">AMZ FC Site</label>
                    <select
                      value={form.first_drop_site_id ?? ''}
                      onChange={e => handleSiteChange(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    >
                      <option value="">-- Select site --</option>
                      {sites.map(s => (
                        <option key={s.id} value={s.id}>{s.code} - {s.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Site Address</label>
                    <input
                      type="text"
                      value={form.first_drop_address}
                      onChange={e => setForm(p => ({ ...p, first_drop_address: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Destination Postcode</label>
                    <input
                      type="text"
                      value={form.destination_postcode}
                      onChange={e => setForm(p => ({ ...p, destination_postcode: e.target.value.toUpperCase() }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm uppercase focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Single Journey</label>
                    <div className="flex items-center gap-4 mt-1">
                      <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="radio"
                          name="single_journey"
                          checked={form.single_journey}
                          onChange={() => setForm(p => ({ ...p, single_journey: true }))}
                          className="accent-blue-600"
                        />
                        Yes
                      </label>
                      <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="radio"
                          name="single_journey"
                          checked={!form.single_journey}
                          onChange={() => setForm(p => ({ ...p, single_journey: false }))}
                          className="accent-blue-600"
                        />
                        No
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Vehicle To Stay</label>
                    <div className="flex items-center gap-4 mt-1">
                      <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="radio"
                          name="vehicle_to_stay"
                          checked={form.vehicle_to_stay}
                          onChange={() => setForm(p => ({ ...p, vehicle_to_stay: true }))}
                          className="accent-blue-600"
                        />
                        Yes
                      </label>
                      <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="radio"
                          name="vehicle_to_stay"
                          checked={!form.vehicle_to_stay}
                          onChange={() => setForm(p => ({ ...p, vehicle_to_stay: false }))}
                          className="accent-blue-600"
                        />
                        No
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Total Distance (mi)</label>
                    <input
                      type="text"
                      value={form.total_distance}
                      onChange={e => setForm(p => ({ ...p, total_distance: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="Auto-calculated"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Empty Distance (mi)</label>
                    <input
                      type="text"
                      value={form.empty_distance}
                      onChange={e => setForm(p => ({ ...p, empty_distance: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="Auto-calculated"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Total Load Weight</label>
                    <input
                      type="text"
                      value={form.total_load_weight}
                      onChange={e => setForm(p => ({ ...p, total_load_weight: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="e.g. 18.5T"
                    />
                  </div>
                </div>
              </div>

              {/* ── Itinerary Builder ──────────────────────────────── */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="text-sm font-semibold text-gray-900 mb-4">Itinerary</h2>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200 text-left">
                        <th className="px-3 py-2 font-medium text-gray-600 w-[90px]">Time</th>
                        <th className="px-3 py-2 font-medium text-gray-600 w-[120px]">Type</th>
                        <th className="px-3 py-2 font-medium text-gray-600 w-[200px]">Site / Location</th>
                        <th className="px-3 py-2 font-medium text-gray-600 w-[70px]">Pallets</th>
                        <th className="px-3 py-2 font-medium text-gray-600">Lat / Lng</th>
                        <th className="px-3 py-2 font-medium text-gray-600 w-[140px]">Notes</th>
                        <th className="px-3 py-2 font-medium text-gray-600 w-[90px]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.itinerary.map((row, idx) => (
                        <tr key={idx} className="border-b border-gray-100">
                          {/* Time */}
                          <td className="px-3 py-1.5">
                            <input
                              type="time"
                              value={row.time}
                              onChange={e => updateRow(idx, 'time', e.target.value)}
                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            />
                          </td>
                          {/* Type */}
                          <td className="px-3 py-1.5">
                            <select
                              value={row.type}
                              onChange={e => updateRow(idx, 'type', e.target.value)}
                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            >
                              <option value="">-- Select --</option>
                              {ITINERARY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </td>
                          {/* Site / Location — FC dropdown or custom postcode */}
                          <td className="px-3 py-1.5">
                            <select
                              value={
                                row.site_id != null && row.site_id > 0
                                  ? String(row.site_id)
                                  : row.site_id === -1
                                    ? 'custom'
                                    : ''
                              }
                              onChange={e => handleItinerarySiteChange(idx, e.target.value)}
                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            >
                              <option value="">-- Select site --</option>
                              {sites.map(s => (
                                <option key={s.id} value={s.id}>{s.code} - {s.name}</option>
                              ))}
                              <option value="custom">Other (enter postcode)</option>
                            </select>
                            {/* Custom postcode input below dropdown when "Other" is selected */}
                            {row.site_id === -1 && (
                              <input
                                type="text"
                                placeholder="Enter postcode"
                                value={row.location}
                                onChange={e => updateRow(idx, 'location', e.target.value.toUpperCase())}
                                onBlur={() => lookupCustomPostcode(idx)}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); lookupCustomPostcode(idx); } }}
                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm mt-1 uppercase focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                              />
                            )}
                            {/* Show location text for HQ/pre-set rows (no site_id, but location set) */}
                            {row.site_id === null && row.location && (
                              <span className="text-xs text-gray-600 block mt-1 truncate" title={row.location}>
                                {row.location}
                              </span>
                            )}
                          </td>
                          {/* Pallets */}
                          <td className="px-3 py-1.5">
                            <input
                              type="number"
                              min="0"
                              value={row.pallets}
                              onChange={e => updateRow(idx, 'pallets', e.target.value)}
                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                              placeholder="0"
                            />
                          </td>
                          {/* Lat / Lng (auto-filled) */}
                          <td className="px-3 py-1.5">
                            <span className="text-xs text-gray-500 block">
                              {row.lat != null && row.lng != null
                                ? `${row.lat.toFixed(5)} / ${row.lng.toFixed(5)}`
                                : '-'}
                            </span>
                          </td>
                          {/* Notes */}
                          <td className="px-3 py-1.5">
                            <input
                              type="text"
                              value={row.notes}
                              onChange={e => updateRow(idx, 'notes', e.target.value)}
                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            />
                          </td>
                          {/* Reorder / Remove */}
                          <td className="px-3 py-1.5 text-center">
                            <div className="flex items-center justify-center gap-0.5">
                              <button
                                onClick={() => moveRow(idx, 'up')}
                                disabled={idx === 0}
                                className="text-gray-400 hover:text-blue-600 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                                title="Move up"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                              </button>
                              <button
                                onClick={() => moveRow(idx, 'down')}
                                disabled={idx === form.itinerary.length - 1}
                                className="text-gray-400 hover:text-blue-600 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                                title="Move down"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                              {form.itinerary.length > 1 && (
                                <button
                                  onClick={() => removeRow(idx)}
                                  className="text-red-400 hover:text-red-600 transition-colors ml-0.5"
                                  title="Remove stop"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <button
                  onClick={addRow}
                  className="mt-3 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                >
                  + Add Stop
                </button>
              </div>

              {/* ── Action Buttons ─────────────────────────────────── */}
              <div className="flex items-center gap-3 pb-5">
                <button
                  onClick={() => handleSave(false)}
                  disabled={saving}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save as Draft'}
                </button>
                <button
                  onClick={() => handleSave(true)}
                  disabled={saving}
                  className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save & Issue'}
                </button>
                {form.id && (
                  <button
                    onClick={() => handlePdf(form.id!)}
                    className="px-5 py-2.5 text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
                  >
                    Export to PDF
                  </button>
                )}
                <button
                  onClick={() => setView('list')}
                  className="px-5 py-2.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>

            </div>
          </>
        )}
      </div>
    </PortalLayout>
  );
}
